// Departman × Modül Görünürlük Ayarları
// Her departman için hangi modüllerde personel listesinde görüneceğini ayarlar.
// Hiç ayar yapılmamış departman → tüm modüllerde görünür (default açık).
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, EmptyState } from '../components/ui';

export default function DepartmentModulesPage() {
  const [modules, setModules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [mappings, setMappings] = useState([]); // [{department_name, module_key}]
  const [loading, setLoading] = useState(false);
  const [savingDept, setSavingDept] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d, mp] = await Promise.all([
        api.get('/department-modules/modules'),
        api.get('/department-modules/departments'),
        api.get('/department-modules'),
      ]);
      setModules(m.data.data || []);
      setDepartments(d.data.data || []);
      setMappings(mp.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Departmanın o modül için izinli olup olmadığı
  const isAllowed = (dept, modKey) => {
    const deptRows = mappings.filter(m => m.department_name === dept);
    if (deptRows.length === 0) return true; // config yok → her modülde görünür (default)
    return deptRows.some(m => m.module_key === modKey);
  };

  const hasConfig = (dept) => mappings.some(m => m.department_name === dept);

  const toggle = async (dept, modKey, currentlyAllowed) => {
    setSavingDept(dept); setMsg(null);
    try {
      // Bu departmanın mevcut modül key'lerini al
      const existing = mappings.filter(m => m.department_name === dept).map(m => m.module_key);
      let next;
      if (existing.length === 0) {
        // Hiç config yok (default açık) → tıklayan kullanıcı bu modülü EXCLUDE etmek istiyor demek
        // Yani: tüm modüller - tıklanan
        next = modules.map(m => m.key).filter(k => k !== modKey);
      } else if (currentlyAllowed) {
        // Şu an izinli → kaldır
        next = existing.filter(k => k !== modKey);
      } else {
        // İzinli değil → ekle
        next = [...existing, modKey];
      }
      await api.post('/department-modules/bulk', { department_name: dept, module_keys: next });
      await load();
      setMsg({ type: 'success', text: dept + ' için ayarlar güncellendi' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Kayıt başarısız' });
    } finally { setSavingDept(null); }
  };

  const resetDept = async (dept) => {
    if (!window.confirm(`"${dept}" departmanı için tüm ayarlar silinecek (tüm modüllerde görünür hale gelecek). Devam edilsin mi?`)) return;
    setSavingDept(dept); setMsg(null);
    try {
      await api.post('/department-modules/bulk', { department_name: dept, module_keys: [] });
      await load();
      setMsg({ type: 'success', text: dept + ' için ayarlar sıfırlandı (tüm modüllerde görünür)' });
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'İşlem başarısız' }); }
    finally { setSavingDept(null); }
  };

  return (
    <div className="stocks-page">
      <div className="stocks-topbar">
        <div className="stocks-topbar-left">
          <h2>Departman × Modül Görünürlüğü</h2>
          <span className="stocks-count-badge">{departments.length} departman</span>
        </div>
        <div className="stocks-topbar-right">
          <Button size="sm" variant="ghost" onClick={load}>↻ Yenile</Button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: 16 }}>
        <div style={{
          padding: '10px 14px', marginBottom: 12,
          background: 'rgba(59,130,246,0.08)', borderLeft: '3px solid var(--info)',
          borderRadius: 4, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          💡 <strong>Mantık:</strong> Bir departman için <strong>hiç işaretlenmemiş satır</strong> varsa, o departmanın personeli <strong>tüm modüllerde</strong> görünür (default açık).
          En az bir kutu işaretlerseniz <strong>sadece işaretli modüllerde</strong> görünürler. <br/>
          🔵 Mavi başlık = hiç ayar yok (default — her yerde görünür) · 🟣 Mor başlık = ayarlanmış (sadece işaretli modüllerde görünür)
        </div>

        {msg && (
          <div style={{
            padding: 10, borderRadius: 4, marginBottom: 12,
            background: msg.type === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(16,185,129,0.08)',
            color: msg.type === 'error' ? '#991b1b' : 'var(--success)',
          }}>{msg.text}</div>
        )}

        {loading && <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}

        {!loading && departments.length === 0 && (
          <EmptyState
            title="Henüz departman tanımlı değil"
            description="Personel kartlarında 'Departman' alanı doldurulan personeller bu sayfada otomatik listelenecek."
            compact
          />
        )}

        {!loading && departments.length > 0 && (
          <table className="stocks-table" style={{ minWidth: 800 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Departman</th>
                {modules.map(m => (
                  <th key={m.key} style={{ textAlign: 'center', minWidth: 110 }}>{m.label}</th>
                ))}
                <th style={{ width: 100, textAlign: 'right' }}>Eylem</th>
              </tr>
            </thead>
            <tbody>
              {departments.map(dept => {
                const configured = hasConfig(dept);
                return (
                  <tr key={dept} className="stocks-table-row">
                    <td>
                      <strong style={{ color: configured ? 'var(--accent-primary, #7c3aed)' : 'var(--info)' }}>
                        {configured ? '🟣 ' : '🔵 '}{dept}
                      </strong>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {configured ? 'Kısıtlı görünüm' : 'Tüm modüllerde görünür'}
                      </div>
                    </td>
                    {modules.map(m => {
                      const allowed = isAllowed(dept, m.key);
                      return (
                        <td key={m.key} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={allowed}
                            disabled={savingDept === dept}
                            onChange={() => toggle(dept, m.key, allowed)}
                            style={{ width: 18, height: 18, cursor: 'pointer', accentColor: configured ? 'var(--accent-primary, #7c3aed)' : 'var(--info)' }}
                          />
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'right' }}>
                      {configured && (
                        <Button variant="ghost" size="sm" onClick={() => resetDept(dept)} disabled={savingDept === dept}>
                          Sıfırla
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
